import { createSelector, createSlice } from '@reduxjs/toolkit';
import { sortItems } from '@utils/functions.js';

const initialState = {
  isLoading: false,
  isActive: false,
  id: 0,
  uid: 0,
  sid: 0,

  blockContent: {
    faqs: [],
    heading: {
      title: '',
      description: '',
      textAlign: 'left',
      image: '',
      divider: '',
      position: '',
    },
  },
  itemDesign: {},
  containerDesign: {},
  previewDesign: {},
  blockPreview: {
    isActive: false,
    view: 'compact',
    thumbnail: null,
    title: '',
    description: '',
    button: '',
    label: '',
    timeStart: null,
  },
  customSchema: null,
};

export const faqSlice = createSlice({
  name: 'faq',
  initialState,
  reducers: {
    faqStore: (state, action) => {
      const { id, isActive, sid, uid, data, preview } = action.payload;

      const block = data ?? {};
      const { blockContent, blockDesign } = block;
      const { itemDesign, containerDesign, previewDesign, customSchema } = blockDesign;

      state.isLoading = false;
      state.id = id ?? 0;
      state.isActive = isActive;
      state.sid = sid;
      state.uid = uid;

      state.itemDesign = itemDesign ?? {};
      state.containerDesign = containerDesign ?? {};
      state.previewDesign = previewDesign ?? {};
      state.customSchema = customSchema ?? null;
      state.blockPreview = {
        isActive: preview?.isActive || false,
        thumbnail: preview?.thumbnail ?? null,
        view: preview?.view ?? 'compact',
        title: preview?.title ?? '',
        description: preview?.description ?? '',
        button: preview?.button ?? '',
        label: preview?.label ?? '',
        timeStart: preview?.timeStart ?? null,
      };

      state.blockContent = {
        faqs: blockContent.faqs ?? [],
        heading: {
          title: blockContent.heading.title ?? '',
          description: blockContent.heading.description ?? '',
          textAlign: blockContent.heading.textAlign ?? 'left',
          image: blockContent.heading.image ?? '',
          divider: blockContent.heading.divider ?? '',
          position: blockContent.heading.position ?? '',
        },
      };
    },
    faqClear: () => initialState,

    faqUpdateIsLoading: (state, action) => {
      state.isLoading = action.payload;
    },
    faqUpdateIsActive: (state, action) => {
      state.isActive = action.payload;
    },
    faqUpdateBlockContent: (state, action) => {
      state.blockContent = action.payload;
    },
    faqAddItem: (state, action) => {
      const item = action.payload;
      if (!item) return;
      const currentFaqs = Array.isArray(state.blockContent?.faqs) ? state.blockContent.faqs : [];
      state.blockContent.faqs = [...currentFaqs, item];
    },
    faqUpdateItems: (state, action) => {
      state.blockContent.faqs = Array.isArray(action.payload) ? action.payload : [];
    },
    faqUpdateCustomSchema: (state, action) => {
      state.customSchema = action.payload ?? null;
    },
    faqUpdatePreviewActive: (state, action) => {
      state.blockPreview.isActive = action.payload;
    },
    faqUpdateBlockPreviewView: (state, action) => {
      state.blockPreview.view = action.payload;
    },
    faqUpdateBlockPreviewContent: (state, action) => {
      const { field, value } = action.payload;
      state.blockPreview[field] = value;
    },
    faqUpdateBlockPreviewDesign: (state, action) => {
      const { field, value } = action.payload;
      state.previewDesign[field] = value;
    },
  },
});

export const {
  faqStore,
  faqClear,
  faqUpdateIsLoading,
  faqUpdateIsActive,
  faqUpdateBlockContent,
  faqAddItem,
  faqUpdateItems,
  faqUpdateCustomSchema,
  faqUpdatePreviewActive,
  faqUpdateBlockPreviewView,
  faqUpdateBlockPreviewContent,
  faqUpdateBlockPreviewDesign,
} = faqSlice.actions;

export const selectFaqIsLoading = (state) => state.faq.isLoading;

export const selectFaqId = (state) => state.faq.id;
export const selectFaqUID = (state) => state.faq.uid;
export const selectFaqSID = (state) => state.faq.sid;

export const selectFaqBlockContent = (state) => state.faq.blockContent;
export const selectFaqSortItems = createSelector([selectFaqBlockContent], ({ faqs }) => {
  return sortItems(faqs);
});
export const selectFaqItemDesign = (state) => state.faq.itemDesign;
export const selectFaqContainerDesign = (state) => state.faq.containerDesign;
export const selectFaqCustomSchema = (state) => state.faq.customSchema;
export const selectFaqPreviewContent = (state) => state.faq.blockPreview;
export const selectFaqPreviewDesign = (state) => state.faq.previewDesign;
export const selectFaqPreviewActive = (state) => state.faq.blockPreview.isActive;
export const selectFaqPreviewView = (state) => state.faq.blockPreview.view;

export default faqSlice.reducer;
